import type { Task, TaskStatus, TaskPriority } from '../types/project';
import type { Person } from '../types/people';
import { AssigneesDisplay } from './AssigneeDropdown';
import TaskAssigneesPicker from './TaskAssigneesPicker';
import { formatDateDDMMYYYY } from '../utils/date';
import { TASK_STATUSES, STATUS_LABEL, STATUS_BADGE } from '../utils/taskStatus';
import { projectLabel } from '../utils/task';

const PRIORITY_COLOR: Record<string, string> = {
  LOW: 'var(--color-text-muted)',
  MEDIUM: 'var(--color-blue-dark)',
  HIGH: 'var(--color-coral)',
};

interface TaskListProps {
  tasks: Task[];
  people?: Person[];
  onTaskClick: (task: Task) => void;
  onAddTask?: () => void;
  onCancelTask?: (task: Task) => void;
  onStatusChange?: (taskId: string, status: TaskStatus) => void;
  onPriorityChange?: (taskId: string, priority: TaskPriority) => void;
  onAssigneesChange?: (taskId: string, assigneeIds: string[]) => void;
  showAddButton?: boolean;
}

export default function TaskList({
  tasks,
  people = [],
  onTaskClick,
  onAddTask,
  onCancelTask,
  onStatusChange,
  onPriorityChange,
  onAssigneesChange,
  showAddButton = true,
}: TaskListProps) {
  const rootTasks = tasks.filter((t) => !t.parent_task_id);
  const showProjectColumn = rootTasks.some((t) => t.theme_name);

  return (
    <div>
      {showAddButton && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
          <button
            type="button"
            onClick={onAddTask}
            style={{
              padding: '10px 20px',
              background: 'var(--color-blue-dark)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius)',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Add
          </button>
        </div>
      )}
      {rootTasks.length === 0 ? (
        <p style={{ color: 'var(--color-text-secondary)', padding: '24px 0' }}>No tasks.</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {rootTasks.map((task) => (
            <li
              key={task.id}
              role="button"
              tabIndex={0}
              onClick={() => onTaskClick(task)}
              onKeyDown={(e) => e.key === 'Enter' && onTaskClick(task)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                padding: '14px 16px',
                marginBottom: '8px',
                background: 'white',
                cursor: 'pointer',
                transition: 'box-shadow 200ms',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <span style={{ flex: 1, fontWeight: 500, minWidth: 0 }}>{task.title}</span>
              {showProjectColumn && (
                <span
                  title={projectLabel(task)}
                  style={{
                    fontSize: '13px',
                    color: 'var(--color-text-secondary)',
                    width: '180px',
                    flexShrink: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {projectLabel(task) || '—'}
                </span>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0, marginLeft: '48px' }}>
                <select
                  value={task.status}
                  onChange={(e) => {
                    e.stopPropagation();
                    onStatusChange?.(task.id, e.target.value as TaskStatus);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    fontSize: '12px',
                    padding: '4px 8px',
                    borderRadius: 'var(--radius)',
                    background: STATUS_BADGE[task.status].bg,
                    color: STATUS_BADGE[task.status].color,
                    fontWeight: 500,
                    width: '110px',
                    border: 'none',
                    cursor: 'pointer',
                    appearance: 'menulist',
                  }}
                >
                  {TASK_STATUSES.map((s) => (
                    <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                  ))}
                </select>
                <select
                  value={task.priority ?? 'MEDIUM'}
                  onChange={(e) => {
                    e.stopPropagation();
                    onPriorityChange?.(task.id, e.target.value as TaskPriority);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    fontSize: '12px',
                    padding: '4px 8px',
                    borderRadius: 'var(--radius)',
                    color: task.priority ? PRIORITY_COLOR[task.priority] ?? 'var(--color-text-muted)' : 'var(--color-text-muted)',
                    width: '72px',
                    border: '1px solid var(--color-border)',
                    background: 'white',
                    cursor: 'pointer',
                    appearance: 'menulist',
                  }}
                >
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                </select>
                <span
                  style={{
                    fontSize: '13px',
                    color: 'var(--color-text-muted)',
                    width: '96px',
                    textAlign: 'left',
                  }}
                >
                  {task.due_date
                    ? new Date(task.due_date) < new Date() ? (
                        <span style={{ color: 'var(--color-coral)' }}>{formatDateDDMMYYYY(task.due_date)}</span>
                      ) : (
                        formatDateDDMMYYYY(task.due_date)
                      )
                    : '—'}
                </span>
                <span
                  style={{
                    fontSize: '13px',
                    color: 'var(--color-text-muted)',
                    width: '96px',
                    textAlign: 'right',
                  }}
                >
                  {people.length > 0 ? (
                    onAssigneesChange ? (
                      <TaskAssigneesPicker
                        task={task}
                        people={people}
                        onChange={(ids) => onAssigneesChange(task.id, ids)}
                      />
                    ) : (
                      <AssigneesDisplay
                        people={people}
                        assigneeIds={
                          task.assignee_ids && task.assignee_ids.length > 0
                            ? task.assignee_ids
                            : task.assignee
                            ? [task.assignee]
                            : []
                        }
                      />
                    )
                  ) : (
                    task.assignee ?? '—'
                  )}
                </span>
                {onCancelTask && (
                  <button
                    type="button"
                    aria-label="Cancel task"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCancelTask(task);
                    }}
                    style={{
                      padding: '4px',
                      fontSize: '18px',
                      lineHeight: 1,
                      color: 'var(--color-text-muted)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
